import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useLocation } from "../lib/locationContext";
import { Search, Star, MapPin, Sparkles } from "lucide-react";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import AdBanner from "../components/AdBanner";

const Restaurants = () => {
  const [params, setParams] = useSearchParams();
  const [restaurants, setRestaurants] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const { location, locations } = useLocation();
  const currentLoc = locations.find(l => l.slug === location);
  const q = params.get("q") || "";
  const category = params.get("category") || "";
  const [searchInput, setSearchInput] = useState(q);

  useEffect(() => {
    api.get("/categories").then(r => setCategories(r.data));
  }, []);

  useEffect(() => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (q) qs.set("q", q);
    if (category) qs.set("category", category);
    if (location) qs.set("location", location);
    api.get(`/restaurants?${qs.toString()}`).then(r => {
      setRestaurants(r.data);
      setLoading(false);
    });
  }, [q, category, location]);

  const onSubmit = (e) => {
    e.preventDefault();
    const next = new URLSearchParams(params);
    if (searchInput) next.set("q", searchInput); else next.delete("q");
    setParams(next);
  };

  const setCategory = (slug) => {
    const next = new URLSearchParams(params);
    if (slug) next.set("category", slug); else next.delete("category");
    setParams(next);
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12" data-testid="restaurants-page">
      <div className="mb-8">
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Discover</p>
        <h1 className="font-heading text-4xl sm:text-5xl font-black mt-2">Restoran {currentLoc?.name || ""}</h1>
        <p className="text-muted-foreground mt-2">{restaurants.length} restoran ditemukan</p>
      </div>

      <form onSubmit={onSubmit} className="flex gap-2 mb-6 max-w-2xl">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            data-testid="restaurants-search-input"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Cari restoran..."
            className="pl-12 h-12 rounded-full bg-card"
          />
        </div>
        <Button type="submit" className="h-12 px-6 rounded-full" data-testid="restaurants-search-btn">Cari</Button>
      </form>

      <div className="mb-8">
        <AdBanner placement="search_top" />
      </div>

      <div className="flex flex-wrap gap-2 mb-10" data-testid="category-filters">
        <Badge
          onClick={() => setCategory("")}
          variant={!category ? "default" : "secondary"}
          className="cursor-pointer py-1.5 px-3"
          data-testid="filter-all"
        >
          Semua
        </Badge>
        {categories.map(c => (
          <Badge
            key={c.slug}
            onClick={() => setCategory(c.slug)}
            variant={category === c.slug ? "default" : "secondary"}
            className="cursor-pointer py-1.5 px-3"
            data-testid={`filter-${c.slug}`}
          >
            {c.icon} {c.name}
          </Badge>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-20 text-muted-foreground">Memuat...</div>
      ) : restaurants.length === 0 ? (
        <div className="text-center py-20">
          <p className="text-muted-foreground">Tidak ada restoran ditemukan.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="restaurants-grid">
          {restaurants.map(r => {
            const isFeatured = r.featured_until && new Date(r.featured_until) > new Date();
            return (
            <Link key={r.id} to={`/restaurants/${r.slug}`} className="warm-card group" data-testid={`restaurant-${r.slug}`}>
              <div className="aspect-[4/3] overflow-hidden bg-muted relative">
                <img src={r.cover_image} alt={r.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                {isFeatured && (
                  <div className="absolute top-3 left-3 bg-gradient-to-r from-amber-500 to-primary text-white px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider flex items-center gap-1">
                    <Sparkles className="w-3 h-3" /> Featured
                  </div>
                )}
              </div>
              <div className="p-5">
                <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
                  <Badge variant="secondary" className="text-[10px] uppercase tracking-wider">{r.category?.replace("-", " ")}</Badge>
                  {r.price_range && <span>{r.price_range}</span>}
                </div>
                <h3 className="font-heading font-bold text-lg leading-tight">{r.name}</h3>
                <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{r.description}</p>
                <div className="flex items-center justify-between mt-4 text-sm border-t border-border pt-3">
                  <div className="flex items-center gap-1">
                    <Star className="w-4 h-4 fill-primary text-primary" />
                    <span className="font-semibold">{r.avg_rating?.toFixed(1) || "—"}</span>
                    <span className="text-xs text-muted-foreground">({r.review_count})</span>
                  </div>
                  <span className="flex items-center gap-1 text-xs text-muted-foreground">
                    <MapPin className="w-3 h-3" /> {currentLoc?.name?.split(" ")[0] || ""}
                  </span>
                </div>
              </div>
            </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Restaurants;
